import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api, { formatApiError } from '../api';
import { useAuth } from '../AuthContext';
import { useNoIndex } from '../ProtectedRoute';

const roleTarget = (role) =>
  role === 'admin' ? '/admin' : role === 'technicien' ? '/technicien' : '/espace-client';

// Gère à la fois /reinitialiser-mot-de-passe et /activer-compte
const ResetPasswordPage = ({ mode = 'reset' }) => {
  useNoIndex();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isActivate = mode === 'activate';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });
      login(data.token, data.user);
      navigate(roleTarget(data.user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4">
        <p className="text-neutral-400" data-testid="reset-no-token">Lien invalide ou incomplet.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4" data-testid="reset-page">
      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-11 h-11 rounded-xl bg-red-700 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">
            {isActivate ? 'Activer mon compte' : 'Nouveau mot de passe'}
          </h1>
          <p className="text-neutral-400 text-sm mt-1">
            {isActivate ? 'Définissez votre mot de passe pour accéder à votre espace.' : 'Choisissez un nouveau mot de passe.'}
          </p>
        </div>
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-neutral-300">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 caractères" required data-testid="reset-password-input"
                className="mt-1.5 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500" />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-neutral-300">Confirmer</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirmer le mot de passe" required data-testid="reset-confirm-input"
                className="mt-1.5 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button"
              className="w-full bg-red-700 hover:bg-red-600 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {isActivate ? 'Activer mon compte' : 'Réinitialiser'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
